// src/components/Finanzas/ModalRegistrarMovimiento.jsx
// Modal con 2 tabs: Gasto / Traslado dinero. Patrón visual de GastoViaticoModal.
// Sin <form> (regla BIOLAB) — botones llaman handlers directos.
//
// Soporta 2 modos:
//   1. Crear (default): tabs Gasto + Traslado visibles, llama registrarGasto/crearTraslado.
//   2. Editar (gastoEditar != null): solo tab Gasto, prefill con sus campos,
//      llama editarGasto(id, ...). El tab Traslado se oculta.

import React, { useState, useEffect } from 'react';
import { Modal, Tab, Tabs, Form, Row, Col, Button, Alert, InputGroup } from 'react-bootstrap';
import Select from 'react-select';
import { FaReceipt, FaExchangeAlt } from 'react-icons/fa';
import { Alertassw } from '../Alertas/Alertassw';
import {
  registrarGasto,
  editarGasto,
  crearTraslado,
  cuentasDeGastos,
  cuentasBolsillosPersonales
} from '../../api/finanzas';
import Swal from 'sweetalert2';
import SelectorOT from './SelectorOT';
import SelectorProveedorTercero from './SelectorProveedorTercero';
import { extractError } from './utils/extractError';
import { todayColombia, toColombiaDateOnly } from '../../utils/fechasColombia';

const formatoCOP = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;

const ModalRegistrarMovimiento = ({ show, onClose, bolsillo, onSuccess, gastoEditar = null, otDefault = null }) => {
  const modoEditar = !!gastoEditar;
  const [tab, setTab] = useState('gasto');

  // Estado tab gasto
  const [categoriasGasto, setCategoriasGasto] = useState([]);

  // El check "¿Tiene factura?" se eliminó. El tercero ahora es obligatorio
  // siempre y los campos factura+proveedor son visibles permanentemente.
  // numeroFactura sigue siendo opcional: se confirma con Swal al guardar.
  const [gastoForm, setGastoForm] = useState({
    cuentaContableGasto: '',
    subcategoria: '',
    fecha: todayColombia(),
    descripcion: '',
    valorTotal: '',
    porcentajeIVA: 0,
    numeroFactura: '',
    proveedor: null,
    proveedorSinCrear: null,
    centroCosto: '',
    ordenTrabajo: null
  });

  // Estado tab traslado (ahora con OT opcional)
  const [otrosBolsillos, setOtrosBolsillos] = useState([]);
  const [trasladoForm, setTrasladoForm] = useState({
    cuenta_destino: '',
    monto: '',
    descripcion: '',
    ordenTrabajo: null
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (show) {
      cargarCategorias();
      cargarBolsillosDestino();
      if (modoEditar && gastoEditar) {
        setTab('gasto');
        setGastoForm({
          cuentaContableGasto: gastoEditar.cuentaContableGasto?._id || gastoEditar.cuentaContableGasto || '',
          subcategoria: gastoEditar.subcategoria || '',
          fecha: gastoEditar.fecha
            ? toColombiaDateOnly(gastoEditar.fecha)
            : todayColombia(),
          descripcion: gastoEditar.descripcion || '',
          valorTotal: gastoEditar.valorTotal ?? '',
          porcentajeIVA: gastoEditar.porcentajeIVA ?? 0,
          numeroFactura: gastoEditar.numeroFactura || '',
          proveedor: gastoEditar.proveedor || null,
          proveedorSinCrear: gastoEditar.proveedorSinCrear || null,
          centroCosto: gastoEditar.centroCosto || '',
          ordenTrabajo: gastoEditar.ordenTrabajo || null
        });
      } else if (!modoEditar && otDefault?._id) {
        // Pre-poblar OT en ambos tabs cuando se abre desde una OT/programación
        const otData = {
          ordenId: otDefault._id,
          numeroOT: otDefault.numeroOrden,
          cliente: otDefault.cliente || null
        };
        setGastoForm(prev => ({ ...prev, ordenTrabajo: otData }));
        setTrasladoForm(prev => ({ ...prev, ordenTrabajo: otData }));
      }
    }
  }, [show, modoEditar, gastoEditar, otDefault?._id]);

  const cargarCategorias = async () => {
    try {
      const resp = await cuentasDeGastos();

      if (resp?.status === 200) setCategoriasGasto(resp.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const cargarBolsillosDestino = async () => {
    try {
      const resp = await cuentasBolsillosPersonales();
      if (resp?.status === 200) {
        // Excluir mi propio bolsillo
        const list = (resp.data.data || []).filter((b) => b._id !== bolsillo._id);
        setOtrosBolsillos(list);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // ============================================
  // Tab Gasto — cálculo automático IVA
  // ============================================
  const subtotalGasto = (() => {
    const total = Number(gastoForm.valorTotal) || 0;
    const pct = Number(gastoForm.porcentajeIVA) || 0;
    if (pct === 0) return total;
    return Math.round((total / (1 + pct / 100)) * 100) / 100;
  })();
  const ivaCalculado = (() => {
    const total = Number(gastoForm.valorTotal) || 0;
    return Math.round((total - subtotalGasto) * 100) / 100;
  })();

  const categoriaSeleccionada = categoriasGasto.find((c) => c._id === gastoForm.cuentaContableGasto);
  const subcategorias = categoriaSeleccionada?.subcategoria || [];

  const handleGuardarGasto = async () => {
    setError(null);

    // Validaciones de cliente
    if (!gastoForm.cuentaContableGasto) return setError('Selecciona una categoría de gasto');
    if (!gastoForm.descripcion) return setError('Agrega una descripción');
    if (!gastoForm.valorTotal || Number(gastoForm.valorTotal) <= 0) return setError('Ingresa un valor mayor a 0');

    // Tercero (proveedor) es OBLIGATORIO siempre — puede ser un Proveedor
    // local, un Proveedor que vino del ERP, o captura manual con nombre+nit.
    const tieneTercero = !!gastoForm.proveedor;
    const tieneManual = !!(gastoForm.proveedorSinCrear?.nombre && gastoForm.proveedorSinCrear?.nit);
    if (!tieneTercero && !tieneManual) {
      return setError('Selecciona el proveedor o captura nombre+NIT manualmente — el tercero es obligatorio.');
    }

    // OT obligatoria depende del BOLSILLO (no de la cuenta de gasto).
    if (bolsillo?.requiereOT && !gastoForm.ordenTrabajo?.ordenId) {
      return setError('Este bolsillo exige asignar una Orden de Trabajo (OT) en cada gasto.');
    }

    if (categoriaSeleccionada?.requiere_centro_costo && !gastoForm.centroCosto) {
      return setError('Esta categoría requiere centro de costo');
    }

    // Si no se ingreso numero de factura, confirmar con Swal antes de guardar.
    if (!gastoForm.numeroFactura || !gastoForm.numeroFactura.trim()) {
      const result = await Swal.fire({
        title: '¿Registrar gasto sin numero de factura?',
        text: 'No ingresaste un numero de factura. Confirma que el tercero te entrego este gasto sin factura formal (o agregalo despues editando el gasto).',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, registrar sin factura',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#0d6efd'
      });
      if (!result.isConfirmed) return;
    }

    const tieneNumFactura = !!(gastoForm.numeroFactura && gastoForm.numeroFactura.trim());
    const payload = {
      bolsillo: bolsillo._id,
      cuentaContableGasto: gastoForm.cuentaContableGasto,
      subcategoria: gastoForm.subcategoria || undefined,
      fecha: gastoForm.fecha,
      descripcion: gastoForm.descripcion,
      valorTotal: Number(gastoForm.valorTotal),
      porcentajeIVA: Number(gastoForm.porcentajeIVA) || 0,
      iva: ivaCalculado,
      tipo_gasto: bolsillo.funcion === 'Viáticos' ? 'VIATICO' : 'CAJA',
      categoria: categoriaSeleccionada?.nombre || 'Otros',
      // tieneFactura ahora se deriva de si hay numero de factura.
      tieneFactura: tieneNumFactura,
      numeroFactura: tieneNumFactura ? gastoForm.numeroFactura.trim() : undefined,
      proveedor: gastoForm.proveedor?._id || undefined,
      proveedorSinCrear: (gastoForm.proveedorSinCrear && !gastoForm.proveedor)
        ? gastoForm.proveedorSinCrear
        : undefined,
      centroCosto: gastoForm.centroCosto || undefined,
      ordenTrabajo: gastoForm.ordenTrabajo || undefined
    };

    setSaving(true);
    try {
      const resp = modoEditar
        ? await editarGasto(gastoEditar._id, payload)
        : await registrarGasto(payload);
      if (resp?.status === 200 || resp?.status === 201) {
        Alertassw(
          'success',
          modoEditar ? 'Gasto actualizado' : 'Gasto registrado',
          modoEditar ? 'Los cambios se guardaron correctamente.' : 'Tu saldo se actualizó.'
        );
        onSuccess();
      } else {
        setError(extractError({ response: resp }, 'Error guardando el gasto'));
      }
    } catch (err) {
      setError(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  // ============================================
  // Tab Traslado
  // ============================================
  const saldoResultanteTraslado = (() => {
    const m = Number(trasladoForm.monto) || 0;
    return (bolsillo.saldoActual || 0) - m;
  })();

  const handleEnviarTraslado = async () => {
    setError(null);
    if (!trasladoForm.cuenta_destino) return setError('Selecciona el destinatario del traslado');
    if (!trasladoForm.monto || Number(trasladoForm.monto) <= 0) return setError('Ingresa un monto mayor a 0');
    if (!trasladoForm.descripcion) return setError('Agrega una descripción');

    const payload = {
      cuenta_origen: bolsillo._id,            // requerido por el modelo TrasladoDinero
      cuenta_destino: trasladoForm.cuenta_destino,
      monto: Number(trasladoForm.monto),
      descripcion: trasladoForm.descripcion,
      ordenTrabajo: trasladoForm.ordenTrabajo || undefined
    };

    setSaving(true);
    try {
      const resp = await crearTraslado(payload);
      if (resp?.status === 200 || resp?.status === 201) {
        Alertassw('success', 'Traslado creado', 'El destinatario debe confirmarlo para que se descuente de tu saldo.');
        onSuccess();
      } else {
        setError(extractError({ response: resp }, 'Error guardando'));
      }
    } catch (err) {
      setError(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      backdrop="static"
      fullscreen="md-down"
      scrollable
      centered
      dialogClassName="movimiento-modal-90vh"
    >

      <Modal.Header closeButton>
        <Modal.Title>
          {modoEditar ? 'Editar gasto' : 'Registrar movimiento'} — {bolsillo.nombre}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ overflowY: 'auto' }}>
        <Tabs activeKey={tab} onSelect={(k) => { setTab(k); setError(null); }} className="mb-3">
          <Tab eventKey="gasto" title={<><FaReceipt className="me-1" /> Gasto</>}>
            <Row className="g-3">
              {/* OT al inicio: siempre visible en el tab gasto. Es obligatoria
                  solo si el BOLSILLO tiene requiereOT=true (politica del bolsillo,
                  no de la cuenta de gasto). Para los demas casos queda opcional. */}
              <Col md={12}>
                <Form.Label className="fw-bold mb-1">
                  Orden de Trabajo (OT){' '}
                  {bolsillo?.requiereOT
                    ? <span className="text-danger">*</span>
                    : <small className="text-muted">(opcional)</small>}
                </Form.Label>
                <SelectorOT
                  value={
                    gastoForm.ordenTrabajo?.ordenId
                      ? {
                          _id: gastoForm.ordenTrabajo.ordenId,
                          numeroOrden: gastoForm.ordenTrabajo.numeroOT,
                          cliente: gastoForm.ordenTrabajo.cliente || null
                        }
                      : null
                  }
                  onChange={(otRaw) =>
                    setGastoForm(prev => ({
                      ...prev,
                      ordenTrabajo: otRaw
                        ? {
                            ordenId: otRaw._id,
                            numeroOT: otRaw.numeroOrden,
                            cliente: otRaw.cliente || null
                          }
                        : null
                    }))
                  }
                />
                <Form.Text className="text-muted">
                  {bolsillo?.requiereOT
                    ? 'Este bolsillo exige asociar una OT a cada gasto.'
                    : 'Puedes asociar una OT si el gasto corresponde a un trabajo especifico.'}
                </Form.Text>
              </Col>

              <Col md={6}>
                <Form.Label>Categoría *</Form.Label>
                <Select
                  options={categoriasGasto.map((c) => ({ value: c._id, label: c.nombre }))}
                  value={
                    gastoForm.cuentaContableGasto
                      ? { value: gastoForm.cuentaContableGasto, label: categoriasGasto.find(c => c._id === gastoForm.cuentaContableGasto)?.nombre }
                      : null
                  }
                  onChange={(opt) => {
                    const catId = opt?.value || '';
                    const cat = categoriasGasto.find(c => c._id === catId);
                    const subs = cat?.subcategoria || [];
                    setGastoForm({
                      ...gastoForm,
                      cuentaContableGasto: catId,
                      subcategoria: subs.length === 1 ? subs[0] : ''
                    });
                  }}
                  isClearable
                  placeholder="Buscar categoría..."
                  noOptionsMessage={() => 'Sin resultados'}
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    control: (base) => ({ ...base, minHeight: '38px' })
                  }}
                />
              </Col>
              {subcategorias.length > 0 && (
                <Col md={6}>
                  <Form.Label>Subcategoría</Form.Label>
                  <Form.Select
                    value={gastoForm.subcategoria}
                    onChange={(e) => setGastoForm({ ...gastoForm, subcategoria: e.target.value })}
                  >
                    <option value="">—</option>
                    {subcategorias.map((s) => <option key={s} value={s}>{s}</option>)}
                  </Form.Select>
                </Col>
              )}
              <Col md={4}>
                <Form.Label>Valor total *</Form.Label>
                <InputGroup>
                  <InputGroup.Text>$</InputGroup.Text>
                  <Form.Control type="number" min="0"
                    value={gastoForm.valorTotal}
                    onChange={(e) => setGastoForm({ ...gastoForm, valorTotal: e.target.value })}
                  />
                </InputGroup>
              </Col>
              <Col md={3}>
                <Form.Label>% IVA</Form.Label>
                <Form.Select
                  value={gastoForm.porcentajeIVA}
                  onChange={(e) => setGastoForm({ ...gastoForm, porcentajeIVA: Number(e.target.value) })}
                >
                  <option value={0}>0% / exento</option>
                  <option value={5}>5%</option>
                  <option value={19}>19%</option>
                </Form.Select>
              </Col>
              <Col md={5}>
                <Form.Label>Subtotal / IVA calculado</Form.Label>
                <div className="text-muted small">
                  Subtotal: <strong>{formatoCOP(subtotalGasto)}</strong> · IVA: <strong>{formatoCOP(ivaCalculado)}</strong>
                </div>
              </Col>
              <Col md={4}>
                <Form.Label>Fecha del gasto *</Form.Label>
                <Form.Control type="date"
                  value={gastoForm.fecha}
                  onChange={(e) => setGastoForm({ ...gastoForm, fecha: e.target.value })}
                />
              </Col>
              <Col md={8}>
                <Form.Label>Descripción *</Form.Label>
                <Form.Control type="text"
                  value={gastoForm.descripcion}
                  onChange={(e) => setGastoForm({ ...gastoForm, descripcion: e.target.value })}
                  placeholder="Ej: Hotel La Sabana 2 noches"
                />
              </Col>
              {/* Factura y tercero son siempre visibles. Numero de factura es
                  opcional (se confirma con Swal al guardar sin numero); el
                  tercero es obligatorio (proveedor real o captura manual). */}
              <Col md={6}>
                <Form.Label>
                  N° factura <small className="text-muted">(opcional)</small>
                </Form.Label>
                <Form.Control type="text"
                  value={gastoForm.numeroFactura}
                  onChange={(e) => setGastoForm({ ...gastoForm, numeroFactura: e.target.value })}
                  placeholder="F-12345 (dejar vacio si no aplica)"
                />
              </Col>
              <Col md={12}>
                <SelectorProveedorTercero
                  value={{
                    proveedor: gastoForm.proveedor,
                    proveedorSinCrear: gastoForm.proveedorSinCrear
                  }}
                  onChange={({ proveedor, proveedorSinCrear }) =>
                    setGastoForm(prev => ({
                      ...prev,
                      proveedor,
                      proveedorSinCrear
                    }))
                  }
                />
              </Col>
              {categoriaSeleccionada?.requiere_centro_costo && (
                <Col md={6}>
                  <Form.Label>Centro de costo *</Form.Label>
                  <Form.Control type="text"
                    value={gastoForm.centroCosto}
                    onChange={(e) => setGastoForm({ ...gastoForm, centroCosto: e.target.value })}
                  />
                </Col>
              )}
            </Row>
          </Tab>

          {!modoEditar && (
            <Tab eventKey="traslado" title={<><FaExchangeAlt className="me-1" /> Traslado dinero</>}>
              <Alert variant="info" className="py-2">
                <small>
                  <strong>Origen:</strong> {bolsillo.nombre} — Saldo disponible {formatoCOP(bolsillo.saldoActual)}
                </small>
              </Alert>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Label>Destinatario *</Form.Label>
                  <Form.Select
                    value={trasladoForm.cuenta_destino}
                    onChange={(e) => setTrasladoForm({ ...trasladoForm, cuenta_destino: e.target.value })}
                  >
                    <option value="">Selecciona...</option>
                    {otrosBolsillos.map((b) => (
                      <option key={b._id} value={b._id}>{b.nombre} ({b.responsable.nombre.charAt(0)} {b.responsable.apellido})</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={6}>
                  <Form.Label>Monto *</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control type="number" min="0"
                      value={trasladoForm.monto}
                      onChange={(e) => setTrasladoForm({ ...trasladoForm, monto: e.target.value })}
                    />
                  </InputGroup>
                  {trasladoForm.monto && (
                    <small className={`${saldoResultanteTraslado < 0 ? 'text-danger' : 'text-muted'}`}>
                      Tu saldo después del traslado: <strong>{formatoCOP(saldoResultanteTraslado)}</strong>
                      {saldoResultanteTraslado < 0 && ' ⚠️ saldo negativo'}
                    </small>
                  )}
                </Col>
                <Col md={12}>
                  <Form.Label>Descripción *</Form.Label>
                  <Form.Control type="text"
                    value={trasladoForm.descripcion}
                    onChange={(e) => setTrasladoForm({ ...trasladoForm, descripcion: e.target.value })}
                    placeholder="Ej: Anticipo viaje Cali"
                  />
                </Col>

                {/* OT opcional asociada al traslado. Util para reflejar
                    contablemente el destino del dinero cuando corresponde
                    a un trabajo concreto. */}
                <Col md={12}>
                  <Form.Label className="fw-bold mb-1">
                    Orden de Trabajo (OT) <small className="text-muted">(opcional)</small>
                  </Form.Label>
                  <SelectorOT
                    value={
                      trasladoForm.ordenTrabajo?.ordenId
                        ? {
                            _id: trasladoForm.ordenTrabajo.ordenId,
                            numeroOrden: trasladoForm.ordenTrabajo.numeroOT,
                            cliente: trasladoForm.ordenTrabajo.cliente || null
                          }
                        : null
                    }
                    onChange={(otRaw) =>
                      setTrasladoForm(prev => ({
                        ...prev,
                        ordenTrabajo: otRaw
                          ? {
                              ordenId: otRaw._id,
                              numeroOT: otRaw.numeroOrden,
                              cliente: otRaw.cliente || null
                            }
                          : null
                      }))
                    }
                  />
                  <Form.Text className="text-muted">
                    Si el traslado financia un trabajo específico, asocialo aqui.
                  </Form.Text>
                </Col>

                <Col md={12}>
                  <Alert variant="warning" className="py-2 mb-0 small">
                    ⓘ El destinatario debe <strong>confirmar el traslado</strong> para que se descuente de tu saldo
                    y se contabilice.
                  </Alert>
                </Col>
              </Row>
            </Tab>
          )}
        </Tabs>
        <br />

        {error && <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancelar</Button>
        {tab === 'gasto' && (
          <Button variant="primary" onClick={handleGuardarGasto} disabled={saving}>
            {saving
              ? (modoEditar ? 'Actualizando...' : 'Guardando...')
              : (modoEditar ? 'Actualizar gasto' : 'Guardar gasto')}
          </Button>
        )}
        {!modoEditar && tab === 'traslado' && (
          <Button variant="primary" onClick={handleEnviarTraslado} disabled={saving}>
            {saving ? 'Enviando...' : 'Enviar traslado'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ModalRegistrarMovimiento;
